import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

// A useState that survives a page refresh and component remounts (e.g.
// navigating away to an edit/detail page and coming back) by persisting to
// sessionStorage under `key`. Intended for view state only - filters, search,
// sort, pagination, dropdown selections - never form inputs, dialog open state,
// or row selection. sessionStorage (not localStorage) so a filter chosen in one
// working session does not silently still apply in a new one days later.
const readValue = <T>(key: string, initialValue: T): T => {
  try {
    const item = sessionStorage.getItem(key);
    return item !== null ? (JSON.parse(item) as T) : initialValue;
  } catch {
    return initialValue;
  }
};

const usePersistedState = <T>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const [value, setValue] = useState<T>(() => readValue(key, initialValue));
  // Skip the write on the initial mount: the value was just read from storage
  // (or equals the default, which needs no persisting), so writing it back is
  // pure churn. Only genuine changes are persisted thereafter.
  const isFirstRun = useRef(true);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    try {
      sessionStorage.setItem(key, JSON.stringify(value));
    } catch {
      // Ignore write failures (quota exceeded / private mode); the value still
      // works in memory for the current mount.
    }
  }, [key, value]);

  return [value, setValue];
};

export default usePersistedState;
